import { useContext, useEffect, useMemo, useState } from 'react';
import { merchantOptionLabel, sortMerchantOptions } from '../../components/ui/merchantOptions';
import { GroupForm, GroupsContext } from '../../context/group/GroupContext';
import { AccessGroup, AccessLevel, ResourceItem } from '../../models/interfaces';
import Message from '../../components/ui/Message';
import FormModal from '../../components/ui/FormModal';
import { useRequiredFields } from '../../hooks/useRequiredFields';
import { LEVEL_ICON, LEVEL_LABEL } from '../../utils/access';
import ResourceTree from './ResourceTree';
import BankOptions from '../../components/ui/BankOptions';

/**
 * Grupos y permisos. Cada administrador (plataforma, banco o comercio) crea grupos de su nivel o de los
 * niveles que administra, con permisos que él puede otorgar. Las plantillas del sistema solo las edita el
 * administrador de plataforma; quitar permisos a la plantilla de administrador de un nivel los quita de todo el nivel.
 */
export const GroupsPage = () => {
    const { groups, levels, banks, merchants, isLoading, loadGroups, loadResources, saveGroup, deleteGroup } = useContext(GroupsContext);

    const [level, setLevel] = useState<AccessLevel | undefined>(undefined);
    const [ownerFilter, setOwnerFilter] = useState<number | ''>('');
    const [search, setSearch] = useState('');

    const [editing, setEditing] = useState<(GroupForm & { readOnly?: boolean; code?: string | null }) | null>(null);
    const [resources, setResources] = useState<ResourceItem[]>([]);
    const [loadingResources, setLoadingResources] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [error, setError] = useState('');
    const [toDelete, setToDelete] = useState<AccessGroup | null>(null);
    const required = useRequiredFields();

    const reload = () => loadGroups({
        level,
        bankId: level === 'BANCO' && ownerFilter ? ownerFilter : null,
        merchantId: level === 'COMERCIO' && ownerFilter ? ownerFilter : null,
    });

    useEffect(() => { reload(); }, [level, ownerFilter]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Pestaña inicial: el nivel más alto que administra
    useEffect(() => {
        if (!level && levels.length > 0) setLevel(levels[0]);
    }, [levels, level]);

    const bankName = (id?: number | null) => banks.find(bank => bank.id === id)?.name ?? `Banco ${id}`;
    const merchantName = (id?: number | null) => {
        const merchant = merchants.find(merchant => merchant.id === id);
        return merchant ? merchantOptionLabel(merchant) : `Comercio ${id}`;
    };
    const sortedMerchants = useMemo(() => sortMerchantOptions(merchants), [merchants]);
    const ownerLabel = (group: AccessGroup) =>
        group.isSystem ? 'Plantilla del sistema'
            : group.merchantId ? merchantName(group.merchantId)
                : group.bankId ? bankName(group.bankId)
                    : 'Plataforma';

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        const byLevel = groups.filter(group => !level || group.level === level);
        if (!term) return byLevel;
        return byLevel.filter(group => [group.name, group.description, ownerLabel(group)].some(v => String(v ?? '').toLowerCase().includes(term)));
    }, [groups, level, search, banks, merchants]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Dueño por defecto: si solo hay una opción (usuario de banco o de comercio), esa
    const defaultOwner = (lvl: AccessLevel) => ({
        bankId: lvl === 'BANCO' ? (ownerFilter || (banks.length === 1 ? banks[0].id : null)) : null,
        merchantId: lvl === 'COMERCIO' ? (ownerFilter || (merchants.length === 1 ? merchants[0].id : null)) : null,
    });

    const openForm = async (group?: AccessGroup) => {
        required.reset();
        setFormError('');
        const lvl = group?.level ?? level ?? levels[0];
        setEditing(group
            ? { id: group.id, name: group.name, description: group.description, level: group.level, bankId: group.bankId, merchantId: group.merchantId, resourceIds: group.resourceIds ?? [], readOnly: !group.editable, code: group.code }
            : { name: '', description: '', level: lvl, ...defaultOwner(lvl), resourceIds: [] });
        setLoadingResources(true);
        setResources(await loadResources(lvl, group?.id));
        setLoadingResources(false);
    };

    const changeFormLevel = async (lvl: AccessLevel) => {
        setEditing(prev => prev && ({ ...prev, level: lvl, ...defaultOwner(lvl), resourceIds: [] }));
        setLoadingResources(true);
        setResources(await loadResources(lvl));
        setLoadingResources(false);
    };

    // El dueño solo se elige al crear (las plantillas del sistema no tienen banco ni comercio)
    const ownerMissing = !!editing && !editing.id && ((editing.level === 'BANCO' && !editing.bankId) || (editing.level === 'COMERCIO' && !editing.merchantId));

    const handleSave = async () => {
        if (!editing) return;
        if (!editing.name.trim() || ownerMissing || editing.resourceIds.length === 0) {
            required.fail();
            setFormError(editing.resourceIds.length === 0 && editing.name.trim() && !ownerMissing
                ? 'Debe seleccionar al menos un permiso.'
                : 'Debe completar los campos requeridos.');
            return;
        }
        setSaving(true);
        const resp = await saveGroup(editing);
        setSaving(false);
        if (resp === 'success') {
            setEditing(null);
            reload();
        } else {
            setFormError(resp);
        }
    };

    const processDelete = async () => {
        const group = toDelete;
        setToDelete(null);
        if (!group) return;
        const resp = await deleteGroup(group.id);
        if (resp !== 'success') setError(resp);
    };

    const ownerOptions = level === 'BANCO' ? banks : level === 'COMERCIO' ? sortedMerchants.map(merchant => ({ ...merchant, name: merchantOptionLabel(merchant) })) : [];

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Grupos y permisos</h3>
                    <p className="text-muted mb-0">Agrupe usuarios y defina qué pueden hacer. Solo puede otorgar permisos que usted tiene.</p>
                </div>
                <button type="button" className="btn btn-primary mt-2 mt-md-0" onClick={() => openForm()} disabled={levels.length === 0}>
                    <i className="bi bi-plus-lg me-1" /> Nuevo grupo
                </button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    {levels.length > 1 && (
                        <ul className="nav nav-tabs mb-3">
                            {levels.map(lvl => (
                                <li className="nav-item" key={lvl}>
                                    <button className={`nav-link ${level === lvl ? 'active' : ''}`} onClick={() => { setLevel(lvl); setOwnerFilter(''); }}>
                                        <i className={`bi ${LEVEL_ICON[lvl]} me-1`} />{LEVEL_LABEL[lvl]}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input className="form-control" type="text" placeholder="Buscar grupo..." value={search} onChange={(e) => setSearch(e.target.value)} />
                                {search && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setSearch('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                        {ownerOptions.length > 1 && (
                            <div className="col-md-4">
                                <select className="form-select" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value ? Number(e.target.value) : '')}>
                                    <option value="">{level === 'BANCO' ? 'Todos los bancos' : 'Todos los comercios'}</option>
                                    {ownerOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className='table-responsive'>
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Grupo</th>
                                    <th>Pertenece a</th>
                                    <th style={{ textAlign: 'center' }}>Permisos</th>
                                    <th style={{ textAlign: 'center' }}>Usuarios</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={5} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : filtered.length > 0 ? filtered.map(group => (
                                    <tr key={group.id}>
                                        <td>
                                            <div className="fw-medium">{group.name}</div>
                                            {group.description && <div className="small text-muted">{group.description}</div>}
                                        </td>
                                        <td>
                                            {group.isSystem
                                                ? <span className="badge rounded-pill bg-secondary-subtle text-secondary-emphasis"><i className="bi bi-lock me-1" />Plantilla del sistema</span>
                                                : ownerLabel(group)}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{group.resourceIds?.length ?? 0}</td>
                                        <td style={{ textAlign: 'center' }}>{group.users ?? 0}</td>
                                        <td style={{ textAlign: 'center', width: 110 }}>
                                            {group.editable ? (
                                                <>
                                                    <button type="button" className="btn btn-sm btn-primary" title="Editar grupo" onClick={() => openForm(group)}>
                                                        <i className="bi bi-pencil" />
                                                    </button>
                                                    {group.deletable !== false && (
                                                        <button type="button" className="btn btn-sm btn-danger ms-1" title="Eliminar grupo" onClick={() => setToDelete(group)}>
                                                            <i className="bi bi-trash" />
                                                        </button>
                                                    )}
                                                </>
                                            ) : (
                                                <button type="button" className="btn btn-sm btn-outline-secondary" title="Ver permisos" onClick={() => openForm(group)}>
                                                    <i className="bi bi-eye" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan={5} className='text-center py-4 text-muted'>
                                        {search ? 'No se encontraron grupos para la búsqueda.' : 'No existen grupos en este nivel.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {editing && (
                <FormModal
                    title={editing.readOnly ? `Permisos: ${editing.name}` : editing.id ? 'Editar grupo' : 'Nuevo grupo'}
                    subtitle={`${LEVEL_LABEL[editing.level]}${editing.readOnly ? ' · solo lectura' : ''}`}
                    size="lg"
                    onClose={() => setEditing(null)}
                    onSave={editing.readOnly ? undefined : handleSave}
                    saving={saving}
                    error={formError}
                >
                    {!editing.readOnly && (
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label htmlFor="groupName" className="form-label">Nombre *</label>
                                <input id="groupName" className={`form-control${required.mark(!editing.name.trim())}`} maxLength={50} autoFocus
                                    value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                                <div className="invalid-feedback">Campo requerido</div>
                            </div>
                            {!editing.id && levels.length > 1 && (
                                <div className="col-md-6">
                                    <label htmlFor="groupLevel" className="form-label">Nivel *</label>
                                    <select id="groupLevel" className="form-select" value={editing.level} onChange={(e) => changeFormLevel(e.target.value as AccessLevel)}>
                                        {levels.map(lvl => <option key={lvl} value={lvl}>{LEVEL_LABEL[lvl]}</option>)}
                                    </select>
                                </div>
                            )}
                            {!editing.id && editing.level === 'BANCO' && banks.length > 1 && (
                                <div className="col-md-6">
                                    <label htmlFor="groupBank" className="form-label">Banco *</label>
                                    <select id="groupBank" className={`form-select${required.mark(!editing.bankId)}`} value={editing.bankId ?? ''}
                                        onChange={(e) => setEditing({ ...editing, bankId: Number(e.target.value) || null })}>
                                        <option value="">Seleccione el banco</option>
                                        <BankOptions banks={banks} />
                                    </select>
                                    <div className="invalid-feedback">Campo requerido</div>
                                </div>
                            )}
                            {!editing.id && editing.level === 'COMERCIO' && merchants.length > 1 && (
                                <div className="col-md-6">
                                    <label htmlFor="groupMerchant" className="form-label">Comercio *</label>
                                    <select id="groupMerchant" className={`form-select${required.mark(!editing.merchantId)}`} value={editing.merchantId ?? ''}
                                        onChange={(e) => setEditing({ ...editing, merchantId: Number(e.target.value) || null })}>
                                        <option value="">Seleccione el comercio</option>
                                        {sortedMerchants.map(merchant => <option key={merchant.id} value={merchant.id}>{merchantOptionLabel(merchant)}</option>)}
                                    </select>
                                    <div className="invalid-feedback">Campo requerido</div>
                                </div>
                            )}
                            <div className="col-12">
                                <label htmlFor="groupDescription" className="form-label">Descripción</label>
                                <input id="groupDescription" className="form-control" maxLength={100}
                                    value={editing.description ?? ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
                            </div>
                        </div>
                    )}

                    {!editing.readOnly && (editing.code === 'bank-admin' || editing.code === 'merchant-admin') && (
                        <div className="alert alert-warning small py-2">
                            <i className="bi bi-exclamation-triangle me-1" />
                            Esta plantilla define los permisos máximos del nivel {LEVEL_LABEL[editing.level].toLowerCase()}. Los permisos que quite también se quitarán de todos los grupos de ese nivel.
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-semibold">Permisos {!editing.readOnly && '*'}</span>
                        {!editing.readOnly && <span className="small text-muted">{editing.resourceIds.length} seleccionado(s)</span>}
                    </div>
                    {loadingResources
                        ? <div className="text-center py-3"><span className="spinner-border spinner-border-sm me-2" />Cargando permisos...</div>
                        : <ResourceTree resources={resources} selected={editing.resourceIds} readOnly={editing.readOnly}
                            onChange={(resourceIds) => setEditing({ ...editing, resourceIds })} />}
                </FormModal>
            )}

            {error && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {toDelete && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Desea eliminar el grupo "${toDelete.name}"?`}
                    closeDialog={() => setToDelete(null)}
                    showCancel={true}
                    handleCancel={() => setToDelete(null)}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    );
};
